import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import api from '../utils/api';

const Notifications = () => {
  const { language, isRTL } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const hasMarkedAsReadRef = useRef(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    hasMarkedAsReadRef.current = false; // Reset flag when fetching new notifications
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Mark notifications as read when they become visible on screen
  useEffect(() => {
    if (notifications.length === 0 || loading || hasMarkedAsReadRef.current) {
      return;
    }

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = (user._id || user.id)?.toString();
    if (!userId) {
      return;
    }

    // Find unread notifications that are relevant to this user
    const unreadNotifications = notifications.filter(n => {
      // Check if notification is relevant to this user
      const isRelevant = user.role === 'admin' || user.role === 'staff' ||
        n.isForAll ||
        (n.recipients && n.recipients.some(r => {
          const recipientId = r._id?.toString() || r?.toString() || (typeof r === 'string' ? r : null);
          return recipientId === userId;
        }));

      if (!isRelevant) return false;

      // Check if already read - if readBy is empty, it's unread
      if (!n.readBy || n.readBy.length === 0) return true;

      const hasRead = n.readBy.some(r => {
        const readUserId = r.user?._id?.toString() || r.user?.toString() || (typeof r.user === 'string' ? r.user : null);
        return readUserId === userId;
      });
      return !hasRead;
    });

    // Mark each unread notification as read by fetching it individually
    // This will mark it as read for the current user only
    if (unreadNotifications.length > 0) {
      hasMarkedAsReadRef.current = true; // Set flag to prevent re-running

      // Use Promise.all to mark all as read, then update state directly
      Promise.all(
        unreadNotifications.map(notification =>
          api.get(`/notifications/${notification._id}`).catch(() => null)
        )
      ).then(results => {
        // Update notifications state with the updated read status
        setNotifications(prev => {
          return prev.map(n => {
            const result = results.find(r => r?.data?._id === n._id);
            return result?.data || n;
          });
        });
      });
    } else {
      hasMarkedAsReadRef.current = true; // No unread notifications, mark as processed
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications.length, loading]); // Only run when notifications are first loaded

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
  };

  const getUnreadCount = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = (user._id || user.id)?.toString();
    if (!userId) return 0;
    const userRole = user.role;

    // Filter notifications that are relevant to this user
    const relevantNotifications = notifications.filter(n => {
      if (userRole === 'admin' || userRole === 'staff') {
        return true; // Admins see all
      } else {
        return n.isForAll || (n.recipients && n.recipients.some(r => {
          const recipientId = r._id?.toString() || r?.toString() || (typeof r === 'string' ? r : null);
          return recipientId === userId;
        }));
      }
    });

    return relevantNotifications.filter(n => {
      // If readBy is empty or undefined, it's unread
      if (!n.readBy || n.readBy.length === 0) return true;

      const hasRead = n.readBy.some(r => {
        const readUserId = r.user?._id?.toString() || r.user?.toString() || (typeof r.user === 'string' ? r.user : null);
        return readUserId === userId;
      });
      return !hasRead;
    }).length;
  };

  const isRead = (notification) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = (user._id || user.id)?.toString();
    if (!userId) return false;

    // If readBy is empty, it's unread
    if (!notification.readBy || notification.readBy.length === 0) return false;

    return notification.readBy.some(r => {
      const readUserId = r.user?._id?.toString() || r.user?.toString() || (typeof r.user === 'string' ? r.user : null);
      return readUserId === userId;
    });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'error':
        return 'bg-red-500/10 text-red-400 border-red-500/30';
      case 'success':
        return 'bg-green-500/10 text-green-400 border-green-500/30';
      case 'warning':
        return 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30';
      case 'announcement':
        return 'bg-primary/10 text-primary border-primary/30';
      default:
        return 'bg-blue-500/10 text-blue-400 border-blue-500/30';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'error':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'success':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'announcement':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-blackC flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-silver">{language === 'en' ? 'Loading notifications...' : 'جاري تحميل الإشعارات...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blackC pt-32 pb-20 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-4xl text-cream mb-2">
            {language === 'en' ? 'Notifications' : 'الإشعارات'}
          </h1>
          <p className="text-gray-light">
            {language === 'en'
              ? `You have ${getUnreadCount()} unread notification${getUnreadCount() !== 1 ? 's' : ''}`
              : `لديك ${getUnreadCount()} إشعار${getUnreadCount() !== 1 ? 'ات' : ''} غير مقروء${getUnreadCount() !== 1 ? 'ة' : ''}`
            }
          </p>
        </div>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <div className="bg-dark border border-gray-dark rounded-xl p-12 text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <p className="text-gray-light text-lg">
              {language === 'en' ? 'No notifications yet' : 'لا توجد إشعارات بعد'}
            </p>
            <p className="text-gray-silver mt-2">
              {language === 'en' ? 'You will see notifications here when they arrive' : 'ستظهر الإشعارات هنا عند وصولها'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {notifications.map((notification) => (
              <div
                key={notification._id}
                onClick={() => handleNotificationClick(notification)}
                className={`bg-dark border rounded-xl p-6 cursor-pointer transition-all hover:border-primary/50 ${!isRead(notification)
                  ? 'border-primary/50 bg-primary/5'
                  : 'border-gray-dark hover:border-gray-light'
                  }`}
              >
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center border ${getTypeColor(notification.type)}`}>
                    {getTypeIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(notification.type)}`}>
                          {language === 'en' ? notification.type : t[notification.type]}
                        </span>
                        {!isRead(notification) && (
                          <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                        )}
                      </div>
                      <span className="text-gray-silver text-sm flex-shrink-0">
                        {new Date(notification.createdAt).toLocaleDateString(language === 'en' ? 'en-US' : 'ar-EG', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    <p className="text-cream text-base leading-relaxed">{notification.message}</p>
                    {/* {notification.sender && (
                      <p className="text-gray-silver text-sm mt-2">
                        {language === 'en' ? 'From:' : 'من:'} {notification.sender.name || notification.sender.email}
                      </p>
                    )} */}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Notification Detail Modal */}
        {selectedNotification && (
          <div
            className="fixed inset-0 bg-blackC/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedNotification(null)}
          >
            <div
              className="bg-dark border border-gray-dark rounded-xl max-w-2xl w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center border ${getTypeColor(selectedNotification.type)}`}>
                    {getTypeIcon(selectedNotification.type)}
                  </div>
                  <div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(selectedNotification.type)}`}>
                      {/* show the type in arabic and english */}
                      {language === 'en' ? selectedNotification.type : t[selectedNotification.type]}
                    </span>
                    <p className="text-gray-silver text-sm mt-1">
                      {new Date(selectedNotification.createdAt).toLocaleString(language === 'en' ? 'en-US' : 'ar-EG')}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedNotification(null)}
                  className="w-8 h-8 rounded-lg bg-charcoal flex items-center justify-center text-gray-light hover:text-cream transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="bg-charcoal border border-gray-dark rounded-lg p-4 mb-4">
                <p className="text-cream text-lg leading-relaxed whitespace-pre-wrap">{selectedNotification.message}</p>
              </div>
              {/* {selectedNotification.sender && (
                <div className="flex items-center gap-2 text-gray-silver text-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span>{language === 'en' ? 'From:' : 'من:'} {selectedNotification.sender.name || selectedNotification.sender.email}</span>
                </div>
              )} */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;

